/*
 * entity-form.store.js — the `entity-form` namespaced Vuex module (recovered
 * `entity-form-store.js`): setup(entityConfig) builds the record Model via the
 * factory, fetch loads it, maybeSave validates then saves, applyAvatar pushes
 * the avatar, then the kind's callbacks[create|update] fire.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (module name "entity-form"; setup/fetch/maybeSave/applyAvatar surface; save
 * order validate -> Model.save() -> applyAvatar() -> callbacks; avatar routed
 * via itemCfg.routes().avatar_upload / avatar_assign), internal impl re-authored.
 * registerModuleSafe-compatible (see installEntityForm()).
 */

import { buildEntityItemModel } from './model-factory.js';
import { request } from './entity-api.js';
import { registerModuleSafe, unregisterModuleSafe } from './entity-api.js';
import { parseRoute } from './model-factory.js';

/** Normalize an entityConfig payload into a live descriptor instance. */
function toDescriptor(entityConfig) {
  if (!entityConfig) return null;
  if (typeof entityConfig === 'function') {
    try { return new entityConfig(); } catch (e) { return entityConfig(); }
  }
  return entityConfig;
}

export const entityFormModule = {
  namespaced: true,

  state: () => ({
    descriptor: null,
    ItemModel: null,
    model: null,
    avatar: null,     // { file } (upload) or { icon_id } (stock icon)
    loading: false,
    saving: false,
    errors: {},       // { fieldId: message }
  }),

  getters: {
    descriptor: (s) => s.descriptor,
    model: (s) => s.model,
    values: (s) => (s.model ? s.model.toObject() : {}),
    saving: (s) => s.saving,
    loading: (s) => s.loading,
    errors: (s) => s.errors,
    isNew: (s) => (s.model ? s.model.isNew() : true),
    sections: (s) => (s.descriptor && typeof s.descriptor.getSections === 'function' ? s.descriptor.getSections() : []),
    fields: (s) => (s.ItemModel ? (new s.ItemModel()).getFields() : []),
    titles: (s) => (s.descriptor && s.descriptor.titles) || {},
  },

  mutations: {
    setDescriptor(s, d) { s.descriptor = d; },
    setItemModel(s, M) { s.ItemModel = M; },
    setModel(s, m) { s.model = m; },
    setAvatar(s, a) { s.avatar = a; },
    setLoading(s, v) { s.loading = !!v; },
    setSaving(s, v) { s.saving = !!v; },
    setErrors(s, e) { s.errors = e || {}; },
  },

  actions: {
    /**
     * setup({ entityConfig|descriptor, id, sourceItem }):
     *   builds the record Model class from the descriptor and instantiates the
     *   record (fetching it when an id is given without a sourceItem).
     */
    async setup({ commit, dispatch, rootGetters }, payload = {}) {
      const descriptor = toDescriptor(payload.entityConfig || payload.descriptor);
      commit('setErrors', {});
      commit('setAvatar', null);

      // built-in kinds synthesize their sections client-side
      if (descriptor && typeof descriptor.fetch === 'function' &&
          (!descriptor.getSections || descriptor.getSections().length === 0)) {
        try { await descriptor.fetch(rootStoreShim(dispatch, rootGetters)); } catch (e) { /* soft */ }
      }
      commit('setDescriptor', descriptor);

      const ItemModel = buildEntityItemModel(descriptor, {
        storeGetters: rootGetters || null,
        decodeContext: {
          employeeById: rootGetters && rootGetters['employees/employeeById'],
          employeeList: rootGetters && rootGetters['employees/employeeList'],
        },
      });
      commit('setItemModel', ItemModel);

      if (payload.sourceItem) {
        commit('setModel', new ItemModel(payload.sourceItem));
      } else if (payload.id != null) {
        commit('setModel', new ItemModel({ id: payload.id }));
        await dispatch('fetch');
      } else {
        commit('setModel', new ItemModel({}));
      }
      return true;
    },

    /** fetch — new Model({id}).fetch() and store the decorated record. */
    async fetch({ state, commit }) {
      if (!state.model) return null;
      commit('setLoading', true);
      try {
        await state.model.fetch();
        commit('setModel', state.model);
        return state.model;
      } finally {
        commit('setLoading', false);
      }
    },

    /**
     * maybeSave(values?) — validate then, if valid, Model.save() ->
     * applyAvatar() -> the kind's callbacks[create|update]. Returns the saved
     * record, or false when validation fails (errors committed).
     */
    async maybeSave({ state, commit, dispatch, rootGetters }, values) {
      if (!state.model) return false;
      if (values && typeof values === 'object') state.model.set(values);

      const { valid, errors } = state.model.validate();
      commit('setErrors', errors);
      if (!valid) return false;

      commit('setSaving', true);
      const isNew = state.model.isNew();
      try {
        const saved = await state.model.save();
        await dispatch('applyAvatar', state.avatar);
        const cbs = (state.descriptor && state.descriptor.callbacks) || {};
        const cb = cbs[isNew ? 'create' : 'update'];
        if (typeof cb === 'function') cb(rootStoreShim(dispatch, rootGetters));
        commit('setModel', state.model);
        return saved;
      } finally {
        commit('setSaving', false);
      }
    },

    /**
     * applyAvatar(avatar) — push a new uploaded file (avatar_upload) or a stock
     * icon (avatar_assign) through the kind's itemCfg avatar routes.
     */
    async applyAvatar({ state }, avatar) {
      if (!avatar || !state.model || !state.descriptor) return null;
      const itemCfg = state.descriptor.itemCfg || {};
      const routes = typeof itemCfg.routes === 'function' ? itemCfg.routes.call(state.descriptor) : {};
      const params = typeof itemCfg.paramsDecorators === 'function' ? itemCfg.paramsDecorators.call(state.descriptor) : {};
      const item = state.model.toObject();

      if (avatar.file && routes.avatar_upload) {
        const spec = parseRoute(routes.avatar_upload);
        const body = typeof params.avatar_upload === 'function'
          ? params.avatar_upload.call(state.descriptor, item, avatar)
          : { id: item.id, file: avatar.file };
        return request(spec.action, body, spec.resultKey);
      }
      if (avatar.icon_id != null && routes.avatar_assign) {
        const spec = parseRoute(routes.avatar_assign);
        const body = typeof params.avatar_assign === 'function'
          ? params.avatar_assign.call(state.descriptor, item, avatar)
          : { id: item.id, icon_id: avatar.icon_id };
        return request(spec.action, body, spec.resultKey);
      }
      return null;
    },

    /** set the pending avatar selection (called by the avatar picker). */
    selectAvatar({ commit }, avatar) { commit('setAvatar', avatar); },

    /** clear the module's working state (on form close). */
    reset({ commit }) {
      commit('setModel', null);
      commit('setItemModel', null);
      commit('setDescriptor', null);
      commit('setAvatar', null);
      commit('setErrors', {});
    },
  },
};

/** A minimal `{ store }` shim so descriptor callbacks/fetch can dispatch root actions. */
function rootStoreShim(dispatch, rootGetters) {
  return {
    store: {
      dispatch: (action, payload) => dispatch(action, payload, { root: true }),
      getters: rootGetters || {},
    },
    dispatch: (action, payload) => dispatch(action, payload, { root: true }),
    getters: rootGetters || {},
  };
}

/** registerModuleSafe-compatible install/uninstall helpers. */
export function installEntityForm(store, path = 'entity-form') {
  return registerModuleSafe(store, path, entityFormModule, { preserveState: false });
}
export function uninstallEntityForm(store, path = 'entity-form') {
  return unregisterModuleSafe(store, path);
}

export default entityFormModule;
